import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { Dropdown, DropdownItem, DropdownList, Input } from '@cokitchen/cokitchen-components';
import { AddIcon, ArrowDownIcon } from 'cokitchen-icons';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import { ReactComponent as TooltipIcon } from 'assets/icons/tooltip.svg';
import MetadataTag from 'components/base/metadata-tag';
import CurrencyInput from 'components/base/currency-input';
import { AddStoreItemType } from 'models/store-item.model';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { storeItemWeightMeasurement } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';

type VendorType = {
  name: string;
  price: string;
  id: string;
};
type Props = {
  storeItem: AddStoreItemType;
  setStoreItem: (storeItem: AddStoreItemType) => void;
  handleDelete: () => void;
  index: number;
  showErrors?: boolean;
};
const isKeyOfStoreItem = (key: string): key is keyof AddStoreItemType => {
  return ['name', 'quantity', 'vendor', 'amount', 'unit', 'category'].includes(key);
};

const AddStoreitemForm = ({ storeItem, setStoreItem, handleDelete, showErrors = false }: Props) => {
  const { storeItemArray } = useInventoryStoreContext();
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const { isFetchingFormIngredientCategoryList, formIngredientCategoryList, formVendorsList } =
    useDashboardContext();

  const unselectedVendors = useMemo(() => {
    const selectedIds = new Set(storeItem.vendors.map((vendor) => vendor.id).filter(Boolean));
    return formVendorsList.filter((formVendor) => !selectedIds.has(formVendor.id));
  }, [formVendorsList, storeItem.vendors]);

  const filteredUnselectedVendors = useMemo(() => {
    const query = vendorSearch.trim().toLowerCase();
    if (!query) return unselectedVendors;
    return unselectedVendors.filter((vendor) => vendor.name?.toLowerCase().includes(query));
  }, [unselectedVendors, vendorSearch]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { name, value } = target;
    if (!isKeyOfStoreItem(name)) return;
    setStoreItem({
      ...storeItem,
      [name]: value
    });
  };

  const handleVendorChange = (item: VendorType, index: number) => {
    const updatedVendors = [...storeItem.vendors];
    updatedVendors[index] = {
      ...updatedVendors[index],
      id: item.id,
      name: item.name,
      price: item.price
    };

    setStoreItem({
      ...storeItem,
      vendors: updatedVendors
    });
  };

  const deleteVendor = (id: string) => {
    setStoreItem({
      ...storeItem,
      vendors: storeItem.vendors.filter((vendor) => vendor.id !== id)
    });
  };

  useEffect(() => {
    setCategory(storeItem.category);
    storeItem.vendors.length === 0 &&
      setStoreItem({
        ...storeItem,
        vendors: [...storeItem.vendors, { name: '', price: '', id: '' }]
      });
  }, []);

  const hasSelectedVendor = useMemo(
    () => storeItem.vendors.some((vendor) => Boolean(vendor.id)),
    [storeItem.vendors]
  );
  const showNameError = showErrors && !storeItem.name?.trim();
  const showVendorError = showErrors && !hasSelectedVendor;

  const isItemValid = (item: AddStoreItemType) => {
    const itemHasVendor = item.vendors.some((vendor) => Boolean(vendor.id));
    if (!itemHasVendor || !item.name) {
      return false;
    }
    return true;
  };

  return (
    <div className='group w-full border-t border-[#EAEBEB] pt-[53px] mt-8 first:border-none  first:mt-0 first:pt-0 relative pb-4'>
      {storeItemArray.length > 1 && (
        <div className={`flex justify-between items-center`}>
          {!isItemValid(storeItem) && (
            <div className='flex gap-1 items-center mb-3 bg-[#FEF0F2] rounded-full py-1 px-3 '>
              <div className='bg-red-5 rounded-full w-4 h-4 ' />
              <p className='text-center text-[#E60A2B] text-sm'> Error </p>
            </div>
          )}

          <button onClick={handleDelete} className='cursor-pointer'>
            <p className='text-sm font-medium text-[#FF0000]'>- Remove Item</p>
          </button>
        </div>
      )}

      <div className='grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
        <div>
          <Input
            design='bordered'
            label='Ingredient Name*'
            name='name'
            placeholder='Ingredient Name'
            value={storeItem.name}
            onChange={handleChange}
          />
          {showNameError && <p className='mt-1 text-sm text-[#e60a2b]'>Name is required</p>}
        </div>

        <div className='relative'>
          <CurrencyInput
            label='Min. Qty'
            placeholder='Min. Qty'
            name='quantity'
            value={storeItem.quantity}
            onWheel={(e) => e.currentTarget.blur()}
            hideCurrency
            hasUnit
            unitValue={storeItem.min_quantity_unit}
            onUnitChange={(value) => {
              setStoreItem({
                ...storeItem,
                min_quantity_unit: value
              });
            }}
            unitOptions={storeItemWeightMeasurement}
            onChange={(value) => {
              setStoreItem({
                ...storeItem,
                quantity: value
              });
            }}
          />
        </div>
        <Input
          design='bordered'
          label={`Unit per (${storeItem.min_quantity_unit})`}
          placeholder='00'
          type='number'
          name='unit'
          onWheel={(e) => e.currentTarget.blur()}
          value={storeItem.unit}
          onChange={handleChange}
        />
        <div className='relative'>
          <p className='text-sm font-medium mb-1'>Category</p>
          <Dropdown
            visible={showCategoryDropdown}
            onClickOutside={() => setShowCategoryDropdown(false)}
            content={
              <div
                className='w-full flex justify-between items-center bg-[#FAFAFA] border-b border-b-[#D5D6D7] py-[15px] px-[14px] cursor-pointer'
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}>
                <p className={`truncate ${!storeItem.category && 'text-[#B5B8BA]'}`}>
                  {category || 'Select category'}
                </p>
                <ArrowDownIcon className='text-[#96999C]' />
              </div>
            }>
            <DropdownList className='min-w-[395px] max-w-[395px] pb-3'>
              {isFetchingFormIngredientCategoryList ? (
                <div className='py-2'>
                  <ActivityIndicator />
                </div>
              ) : formIngredientCategoryList?.length > 0 ? (
                formIngredientCategoryList.map((item) => (
                  <DropdownItem
                    key={item}
                    onClick={() => {
                      setCategory(item);
                      setStoreItem({
                        ...storeItem,
                        category: item
                      });
                      setShowCategoryDropdown(false);
                    }}>
                    {item}
                  </DropdownItem>
                ))
              ) : (
                <div className='py-4'>
                  <h4 className='text-center'>No categories</h4>
                </div>
              )}
              <div className='border-t border-t-[#ECEDEE] pt-4 px-3 mt-3'>
                <div className='flex rounded-md gap-2 bg-[#F7F7F8] h-[46px] w-full p-[10px]'>
                  <div className='w-[98px] h-[24px] flex gap-[2px] bg-[#EAEBEB] rounded-[3px] justify-center items-center'>
                    <AddIcon className='text-[#969A9C]' />
                    <p className='text-[#969A9C] text-xs'>Create new</p>
                  </div>
                  <input
                    type='text'
                    placeholder='Type something'
                    className='w-[55%] h-full bg-transparent outline-none text-sm'
                    value={newCategory}
                    onChange={(e) => {
                      setNewCategory(e.target.value);
                    }}
                  />
                  <div
                    className='w-[56px] h-[24px] flex justify-center cursor-pointer items-center rounded-[3px] shadow-sm border border-[#EAEBEB] bg-white'
                    onClick={() => {
                      setCategory(newCategory);
                      setStoreItem({
                        ...storeItem,
                        category: newCategory
                      });
                    }}>
                    <p className='text-[#5E6164] text-xs font-medium'>Save</p>
                  </div>
                </div>
              </div>
            </DropdownList>
          </Dropdown>
        </div>
        <Input
          design='bordered'
          label='Shelf Life'
          placeholder='0'
          type='number'
          name='shelfLife'
          value={storeItem.shelfLife}
          onChange={(e) => setStoreItem({ ...storeItem, shelfLife: Number(e.target.value) })}
        />
      </div>
      <div className='grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
        {storeItem.vendors.length > 0 &&
          storeItem.vendors.map((vendor, index) => {
            return (
              <Fragment key={vendor.id}>
                <div className='relative'>
                  {index !== 0 && (
                    <button
                      onClick={() => deleteVendor(vendor.id)}
                      className='cursor-pointer absolute right-0'>
                      <p className='text-sm font-medium text-[#FF0000]'>- Remove Vendor</p>
                    </button>
                  )}
                  <div className='flex items-center gap-1 mb-1'>
                    <p className='text-sm font-medium'>Vendor*</p>
                    {index == 0 && (
                      <div>
                        <Dropdown content={<TooltipIcon />} tooltip placement='top'>
                          <div className='bg-white rounded-md p-3 h-[58px] shadow'>
                            <p className='text-gray-9 text-sm'>
                              Select vendors with the highest selling price first
                            </p>
                          </div>
                        </Dropdown>
                      </div>
                    )}
                  </div>
                  <Dropdown
                    content={
                      <div className='flex justify-between items-center bg-[#FAFAFA] border-b border-b-[#D5D6D7] py-[15px] px-[14px] cursor-pointer'>
                        {
                          <Fragment>
                            {vendor.id ? (
                              <div className='flex gap-2 flex-wrap'>
                                <MetadataTag
                                  key={vendor.id}
                                  name={vendor.name}
                                  onDelete={() => {
                                    deleteVendor(vendor.id);
                                  }}
                                  // hasDeleteBtn
                                />
                              </div>
                            ) : (
                              <div className='w-full flex justify-between items-center'>
                                <p className='text-[#B5B8BA] truncate'>Select vendor</p>
                                <ArrowDownIcon className='text-[#96999C]' />
                              </div>
                            )}
                          </Fragment>
                        }
                      </div>
                    }>
                    <DropdownList className='vendors-array-container z-[999]'>
                      <div className=''>
                        <input
                          type='search'
                          placeholder='Search vendors'
                          className='h-[36px] px-4 border-b border-grey-200'
                          value={vendorSearch}
                          onChange={(e) => setVendorSearch(e.target.value)}
                        />
                      </div>
                      <div className=' '>
                        {formVendorsList?.length === 0 ? (
                          <div className='py-3'>
                            <p>No vendors found</p>
                          </div>
                        ) : filteredUnselectedVendors?.length > 0 ? (
                          filteredUnselectedVendors.map((item) => (
                            <DropdownItem key={item.id}>
                              <div
                                className='flex gap-1 items-center w-full'
                                onClick={() => {
                                  handleVendorChange(
                                    { id: item.id, name: item.name, price: '' },
                                    index
                                  );
                                  setVendorSearch('');
                                }}>
                                <span className='text-[#212224]'>{item.name}</span>
                              </div>
                            </DropdownItem>
                          ))
                        ) : (
                          <div className='py-3'>
                            <h4 className='text-center'>No result</h4>
                          </div>
                        )}
                      </div>
                    </DropdownList>
                  </Dropdown>
                  {index === 0 && showVendorError && (
                    <p className='mt-1 text-sm text-[#e60a2b]'>Vendor is required</p>
                  )}
                </div>
                <div className='relative'>
                  <CurrencyInput
                    label='Price'
                    placeholder='00'
                    name='amount'
                    onWheel={(e) => e.currentTarget.blur()}
                    value={vendor.price}
                    onChange={(value) => {
                      handleVendorChange({ id: vendor.id, name: vendor.name, price: value }, index);
                    }}
                  />
                </div>
              </Fragment>
            );
          })}
      </div>
      <div
        className='mt-3 flex gap-[2px] text-[#0080E5] cursor-pointer w-max'
        onClick={() => {
          setStoreItem({
            ...storeItem,
            vendors: [...storeItem.vendors, { name: '', price: '', id: '' }]
          });
        }}>
        <AddIcon className='w-5 h-5' />
        <p className='text-sm font-medium'>Add vendor</p>
      </div>
    </div>
  );
};

export default AddStoreitemForm;
